import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, takeUntil } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'typeahead',
  templateUrl: './typeahead.component.html',
  styleUrls: ['./typeahead.component.scss']
})
export class TypeaheadComponent implements OnInit, OnDestroy, OnChanges {
  private searchTerm$ = new Subject<string>();
  private unsubscribe: Subject<void> = new Subject();
  filteredList: any[];
  searching: boolean;
  firstSearchTerm: string;
  currentSearchTerm: string;
  clickedInside: boolean;
  showMenu: boolean;
  typeaheadOptionIndex: number;
  itemSelectOccured: boolean;

  @Input()
  parentForm: FormGroup;

  @Input()
  controlNameForForm: string;

  @Input()
  defaultValue: string;

  // The list of items that will be displayed in the typeahead drop down
  // example: [{id: '123', name: 'apple', color: 'red'}, {id: '345', name: 'orange', color: 'orange'}]
  @Input()
  list: any[];

  // The label to be displayed above the field
  @Input()
  fieldLabel: string;

  // The text that will be shown as the placeholder of the search box
  @Input()
  placeholderText: string;

  // The property from each object that you want to be displayed
  // example: 'name'
  @Input()
  displayProperty: string;

  // If needed, this is the second property value that can be displayed
  // example: 'color'
  @Input()
  secondaryDisplayProperty: string;

  // Dynamic list indicates if the search list will change based on the entered search value.
  // For example, if you do a type ahead to find a specific user you may not decide not to retrieve
  // an entire list of users before you start typing, but rather pull in a list dynamically from an
  // API as the user starts typing. In this case the parent component would have to handle talking
  // to the api, retrieving valid results and updating the list for the typeahead.
  //
  // If false, the typeahead will just use the original list input.
  @Input()
  dynamicList: boolean;

  // Callback function for when an item from the drop down is clicked on
  @Output()
  itemClicked = new EventEmitter<any>();

  // Callback frunction to be sent to the parent compoent when the search string has changed
  @Output()
  searchTermChanged = new EventEmitter<string>();

  constructor() {
    this.firstSearchTerm = null;
    this.currentSearchTerm = '';
    this.showMenu = false;
    this.typeaheadOptionIndex = -1;
    this.itemSelectOccured = false;
  }

  ngOnInit() {
    this.subscribeToSearchChange();
  }

  /**
   * @description On Changes, copy the list and reset defaults if needed
   */
  ngOnChanges() {
    this.filteredList = JSON.parse(JSON.stringify(this.list));
    this.searching = false;
    this.itemSelectOccured = false;
    if (this.currentSearchTerm === '' || (this.list && this.list.length === 0)) {
      this.searching = false;
      this.list = [];
      this.filteredList = [];
      this.showMenu = false;
    }
  }

  /**
   * @description Emits the clicked item up to the parent component, hides menu
   * @param item
   */
  itemSelected(item: any) {
    this.itemClicked.emit(item);
    this.showMenu = false;
  }

  /**
   * @description Called everytime there is a keyup on the search input field. Either adds the search term
   *              to the searchTerm$ subject or filters the current list based on the search value
   * @param searchTerm
   */
  updateList(searchTerm: string, e: KeyboardEvent) {
    this.navigateTypeaheadOptions(e);
    this.currentSearchTerm = searchTerm;
    if (this.dynamicList && !searchTerm.startsWith(this.firstSearchTerm)) {
      if (searchTerm === '') {
        this.list = [];
        this.filteredList = [];
        this.searching = false;
        this.firstSearchTerm = null;
        this.showMenu = false;
      }
      this.searchTerm$.next(searchTerm);
    } else {
      this.filterList(searchTerm);
      this.showMenu = !this.itemSelectOccured && this.filteredList && this.filteredList.length > 0 ? true : false;
    }
  }

  /**
   * @description: subscribes to the search value and sends the search value to the parent component
   */
  private subscribeToSearchChange() {
    this.searchTerm$
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500), // if typing has stopped for 500 miliseconds
        distinctUntilChanged(), // if value is different than previous value
        tap(term => {
          if (term !== '') {
            this.searchTermChanged.emit(term);
            this.searching = this.currentSearchTerm && this.currentSearchTerm.length > 0 ? true : false;
            this.firstSearchTerm = term;
            this.showMenu = true;
          }

          return;
        })
      )
      .subscribe();
  }

  /**
   * @description Filters the list down to only show the list items whos display property contains the
   *              search string
   * @param searchTerm
   */
  private filterList(searchTerm: string) {
    this.filteredList = this.list.filter(item => {
      const itemProperty = item[this.displayProperty].toLowerCase();
      return itemProperty.includes(searchTerm.toLowerCase());
    });
  }

  /**
   * @description Hides the menu if user clicks outside of menu
   */
  clickOutside() {
    if (this.clickedInside === false) {
      this.closeMenu();
    }
    this.clickedInside = false;
  }

  /**
   * @description Hides menu
   */
  closeMenu() {
    this.showMenu = false;
    this.typeaheadOptionIndex = -1;
  }

  /**
   * @description unsubscribes from observables when component is destroyed
   */
  ngOnDestroy() {
    this.unsubscribe.next();
  }

  /**
   * @name: navigateTypeaheadOptions
   * @description: This function contains the controls for navigating through the typeahead options of the drop-down. The up and down
   *               arrows on the keyboard allow the user to hightlight the value they want to select. Hitting the 'Enter' key
   *               will trigger add the highlighted option to the list of selected options.
   * @param {event} e
   */
  navigateTypeaheadOptions(e) {
    const availableOptions = this.filteredList.length > 0 ? this.filteredList.length : null;

    const numOfItems = this.filteredList.length - 1;

    if (this.showMenu) {
      if (e.keyCode === 40) {
        // Down Arrow
        if (availableOptions) {
          if (this.typeaheadOptionIndex < numOfItems) {
            this.typeaheadOptionIndex++;

            const list = e.target.parentElement.getElementsByClassName('typeahead-drop-down_item-container')[0];
            const selectedNode = list.children[this.typeaheadOptionIndex];
            const container = e.target.parentElement.getElementsByClassName('typeahead-drop-down')[0];
            if (container.offsetHeight < selectedNode.offsetTop + selectedNode.offsetHeight) {
              container.scrollTop += selectedNode.offsetHeight;
            }
          }
        }
      } else if (e.keyCode === 38) {
        // Up Arrow
        if (this.filteredList.length > 0 || numOfItems === 0) {
          this.typeaheadOptionIndex--;
          if (this.typeaheadOptionIndex < 0) {
            this.typeaheadOptionIndex = -1;
          } else {
            const list = e.target.parentElement.getElementsByClassName('typeahead-drop-down_item-container')[0];
            const selectedNode = list.children[this.typeaheadOptionIndex];
            const container = e.target.parentElement.getElementsByClassName('typeahead-drop-down')[0];
            if (container.scrollTop > selectedNode.offsetTop) {
              container.scrollTop -= selectedNode.offsetHeight;
            }
          }
        }
      } else if (e.keyCode === 13) {
        // Enter key
        if (this.typeaheadOptionIndex > -1) {
          const container = e.target.parentElement.getElementsByClassName('typeahead-drop-down_item-container')[0];
          const selectedNode = container.children[this.typeaheadOptionIndex];
          this.itemSelectOccured = true;
          selectedNode.click();
          this.typeaheadOptionIndex = -1;
        } else {
          this.closeMenu();
        }
      }
    }
  }

  closeOnTab(e) {
    // close Menu on Tab
    if (e.keyCode === 9) {
      this.closeMenu();
    }
  }
}
